---
title: Persona configuration reference
summary: Every persona setting — AI/RAG, voice, and avatar — with defaults and ranges.
visibility: authenticated
audience: author-admin
section: Reference
order: 70
last_verified: 2026-10-28
owner: docs
---

# Persona configuration reference

The settings below are grouped the way they appear in **Persona Settings** (the card's
**⋯** menu → **Settings…**). Most authors only touch a few of them; the defaults are
sensible.

## AI settings (RAG)

These control how the persona retrieves and generates answers. They live on the **AI
Settings** tab of Persona Settings. The system prompt itself is authored elsewhere — as
blocks in the Workshop (for example, a **Rules & constraints** block) — and only
*viewed* here: the Workshop's **Advanced** section has a read-only **View composed
prompt →** inspector showing the exact assembled result, with jump-to-edit back to each
block. See [Advanced authoring in the Workshop](/help/advanced/use-the-workshop).

| Setting | Default | Range | What it does |
|---|---|---|---|
| Temperature | 0.7 | 0–2 | How freely the model writes. Lower = closer to the sources. |
| Reply budget (tokens) | 8192 | 1–16384 | The most one reply may spend. Shape length in the persona's instructions. |
| Top K Sources | 5 | 1–20 | How many passages are retrieved per question. |
| Score Threshold | 0.7 | 0–1 | Minimum relevance a passage needs to be used. |
| Graph ID | *(deployment default)* | lowercase; starts/ends with a letter or digit; hyphens between; ≤ 64 chars | Which knowledge graph to retrieve from. |
| RAG Server URL | *(deployment default)* | instance / alias / full URL | Which RAG server to use (override). |
| RAG Server API Key | — | — | Per-persona key for the RAG server, if required. |
| Model | *(server default)* | — | The completion model. Leave default unless overriding. |

## Voice settings

<FeatureGate feature="voice">

Shown on the **Presentation** tab when the persona's mode is *Voice* or *Avatar*.

| Setting | Default | What it does |
|---|---|---|
| Provider | `elevenlabs` | TTS provider: ElevenLabs, Azure Speech, or Kokoro (local). |
| Voice | *(required)* | The provider voice to speak with. |
| Speech Recognition Language | `multi` | The STT language, or multilingual auto-detect. |
| TTS Model | — | ElevenLabs model (e.g. a flash/turbo model). |
| Stability | 0.5 | ElevenLabs: steadiness of delivery (0–1). |
| Similarity | 0.75 | ElevenLabs: closeness to the source voice (0–1). |
| Pronunciation Rules | — | ElevenLabs: up to three pronunciation dictionaries. |

</FeatureGate>

## Avatar settings

<FeatureGate feature="avatars">

Shown on the **Presentation** tab when the persona's mode is *Avatar*.

| Setting | Default | What it does |
|---|---|---|
| Provider | `tavus` | Video avatar provider: Tavus or Anam. |
| Replica ID | — | Tavus: the avatar replica to use (required for Tavus). |
| Anam Avatar / Voice / LLM IDs | — | Anam: the avatar, voice, and (optional) LLM identifiers. |
| Pipeline Mode | `tavus_ai` | Who runs the conversation: the provider's AI, or this product's Expert RAG. |

</FeatureGate>

## See also

- [Connect a persona to its knowledge](/help/how-to/connect-knowledge-rag)
- [Make a persona answer only from its documents](/help/how-to/answer-only-from-documents)
- [Glossary](/help/reference/glossary)
